import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";

import SectionLabel from "../../../app/universalComponents/SectionLabel";
import Alert from "../../../app/universalComponents/Alert";
import Loading from "../../../app/universalComponents/Loading";
import ErrorState from "../../../app/universalComponents/ErrorState";

import KPICards from "./KPICards";
import TimelineWidget from "./TimelineWidget";
import InventoryBreakdownWidget from "./InventoryBreakdownWidget";
import RevenueBreakdownWidget from "./RevenueBreakdownWidget";

import { getEventOverview } from "../dashboardApi";

const DEFAULT_DURATION = "last_7_days";
const DEFAULT_METRIC = "revenue";

const createEmptyChart = () => ({
  labels: [],
  dataset: null,
});

function normaliseChart(chart) {
  if (!chart || typeof chart !== "object") {
    return createEmptyChart();
  }

  return {
    labels: Array.isArray(chart.labels) ? chart.labels : [],
    dataset: chart.dataset ?? null,
  };
}

function isValidOverview(data) {
  return Boolean(
    data &&
      typeof data === "object" &&
      data.totals &&
      typeof data.totals === "object" &&
      data.today &&
      typeof data.today === "object",
  );
}


export default function OverviewTab() {
  const { activeEvent } = useOutletContext();
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState(createEmptyChart);

  const [loading, setLoading] = useState(true);
  const [refreshError, setRefreshError] = useState(false);

  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [metric, setMetric] = useState(DEFAULT_METRIC);

  const activeEventId = activeEvent?.id ?? null;
  const activeEventStatus = activeEvent?.status ?? null;

  /*
   * Reset event-specific state whenever the selected event changes.
   */
  useEffect(() => {
    setDuration(DEFAULT_DURATION);
    setMetric(DEFAULT_METRIC);
    setOverview(null);
    setTimeline(createEmptyChart());
    setRefreshError(false);
    setLoading(Boolean(activeEventId));
  }, [activeEventId]);

  /*
   * Load the overview and refresh it every 30 seconds.
   */
  useEffect(() => {
    if (!activeEventId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let requestInProgress = false;

    async function fetchOverview() {
      /*
       * Prevent interval requests from overlapping if the API takes longer
       * than 30 seconds to respond.
       */
      if (requestInProgress) {
        return;
      }

      requestInProgress = true;

      try {
        const overviewData = await getEventOverview(activeEventId, {
          duration,
          metric,
        });

        if (cancelled) {
          return;
        }

        if (!isValidOverview(overviewData)) {
          throw new Error(
            "The overview API returned an incomplete or invalid response.",
          );
        }

        setOverview(overviewData);
        setTimeline(normaliseChart(overviewData.chart));
        setRefreshError(false);
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error("Failed to load event overview:", {
          eventId: activeEventId,
          duration,
          metric,
          error,
          response: error?.response?.data,
          status: error?.response?.status,
        });

        setRefreshError(true);
      } finally {
        requestInProgress = false;

        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    setLoading(true);
    fetchOverview();

    const intervalId = window.setInterval(fetchOverview, 30000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeEventId, duration, metric]);

  function handleDurationChange(nextDuration) {
    if (!nextDuration || nextDuration === duration) {
      return;
    }

    setDuration(nextDuration);
  }

  function handleMetricChange(nextMetric) {
    if (!nextMetric || nextMetric === metric) {
      return;
    }

    setMetric(nextMetric);
  }

  if (!activeEventId) {
    return (
      <ErrorState>
        Select an event to view its overview.
      </ErrorState>
    );
  }

  if (loading && !overview) {
    return <Loading />;
  }

  if (!overview?.totals || !overview?.today) {
    return (
      <ErrorState>
        Overview data is unavailable.
      </ErrorState>
    );
  }

  const ticketTypes = Array.isArray(overview.ticket_types)
    ? overview.ticket_types
    : [];

  return (
    <>
      {refreshError && (
        <Alert
          tone="neutral"
          className="sticky top-0 z-10 w-full text-center text-sm"
        >
          Couldn't refresh the overview data. The displayed information may be
          out of date.
        </Alert>
      )}

      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-6 gap-4 p-4">
        <KPICards eventStatus={activeEventStatus} totals={overview.totals} today={overview.today}/>

        <div className="col-span-6 grid grid-cols-6 items-stretch gap-4">
          <div className="col-span-6 flex flex-col gap-4 md:col-span-2">
            <RevenueBreakdownWidget ticketTypes={ticketTypes} eventStatus={activeEventStatus}/>
            <InventoryBreakdownWidget ticketTypes={ticketTypes}/>
          </div>

          <article className="col-span-6 flex flex-col md:col-span-4">
            <SectionLabel>Timeline</SectionLabel>

            <div className="flex min-h-80 flex-1 flex-col rounded-sm border p-4">
              <TimelineWidget
                key={activeEventId}
                eventStatus={activeEventStatus}
                chart={timeline}
                duration={duration}
                metric={metric}
                onDurationChange={handleDurationChange}
                onMetricChange={handleMetricChange}
                refreshError={refreshError}
              />
            </div>
          </article>
        </div>
      </div>
    </>
  );
}